import { Box, Flex, IconButton, Icon, Heading, Tooltip } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IoMdCheckmark, IoMdClose } from 'react-icons/io';
import { CellValue } from 'react-table';

import Table, { IndeterminateCheckbox } from 'components/Table';
import { MONTH_DAY_YEAR_TIME_FORMAT } from 'global/appConstants';
import { useParamsQuery } from 'hooks/common/useParamsQuery';
import { useMutateCherryPickShifts } from 'hooks/shifts/useFetchCherryPickShifts';
import { useUpdateShift } from 'hooks/shifts/useUpdateShift';
import { useCompanyInfo, useUserInfo } from 'store/useUserStore';
import toaster from 'utils/toaster';

import ShiftTableFooter from './ShiftTableFooter';

export type TCherryPickAction = 'single-accept' | 'single-decline' | 'bulk-accept' | 'bulk-decline' | null;

const CherryPickShift = () => {
  const searchQuery = useParamsQuery();

  const userInfo = useUserInfo();
  const companyInfo: any = useCompanyInfo();

  const { t } = useTranslation(['common', 'scheduling']);
  const [selectedShifts, setSelectedShifts] = useState<string[]>([]);
  const [cherryPickAction, setCherryPickAction] = useState<TCherryPickAction>(null);

  const { mutateAsync: mutateCherryPickShifts, status: cherryPickShiftsStatus, data } = useMutateCherryPickShifts();
  const { mutateAsync: mutateUpdateShift, status: updateShiftStatus } = useUpdateShift();

  const columns = [
    {
      id: 'selection',
      width: 30,
      Header: ({ getToggleAllRowsSelectedProps }: any) => {
        return <IndeterminateCheckbox {...getToggleAllRowsSelectedProps()} colorScheme="secondary" />;
      },
      Cell: ({ row }: any) => {
        return <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} colorScheme="secondary" />;
      },
    },
    {
      Header: t('common:startDate'),
      Cell: (data: CellValue) => {
        const formattedDate = format(
          new Date(data?.row?.original?.start),
          companyInfo?.companyDateFormat || MONTH_DAY_YEAR_TIME_FORMAT
        );
        return <Box>{formattedDate}</Box>;
      },
    },
    {
      Header: t('common:endDate'),
      accessor: 'end',
      Cell: (data: CellValue) => {
        const formattedDate = format(
          new Date(data?.row?.original?.end),
          companyInfo?.companyDateFormat || MONTH_DAY_YEAR_TIME_FORMAT
        );
        return <Box>{formattedDate}</Box>;
      },
    },
    {
      Header: t('scheduling:scheduleTitle'),
      accessor: 'title',
      Cell: (data: CellValue) => {
        return <Box>{data?.value}</Box>;
      },
    },
    {
      Header: t('common:notes'),
      accessor: 'notes',
      Cell: (data: CellValue) => {
        return <Box>{data?.value || '---'}</Box>;
      },
    },
    {
      Header: t('common:status'),
      accessor: 'status',
      Cell: (data: CellValue) => {
        const result =
          data?.value === null ? (
            <Box color="secondary.400">{t('common:pending')}</Box>
          ) : data?.value ? (
            <Box color="success.500">{t('common:accepted')}</Box>
          ) : (
            <Box color="secondary.700">{t('common:declined')}</Box>
          );

        return result;
      },
    },
    {
      Header: '',
      accessor: 'actions',
      Cell: (data: CellValue) => {
        return (
          <Flex>
            <Tooltip label={t('scheduling:acceptShift')} hasArrow bg="black">
              <IconButton
                aria-label={t('scheduling:acceptShift')}
                bg="rgba(38, 103, 201, 0.1)"
                icon={<Icon as={IoMdCheckmark} height="18px" width="18px" color="primary.300" />}
                size="sm"
                m="1"
                _hover={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                _active={{ bg: 'rgba(38, 103, 201, 0.2)' }}
                isLoading={
                  cherryPickAction === 'single-accept' &&
                  selectedShifts?.includes(data?.row?.original?.id as string) &&
                  updateShiftStatus === 'loading'
                }
                onClick={() => handleAccept(data?.row?.original?.id)}
              />
            </Tooltip>
            <Tooltip label={t('scheduling:declineShift')} hasArrow bg="black">
              <IconButton
                aria-label={t('scheduling:declineShift')}
                bg="rgba(255, 83, 83, 0.1)"
                _hover={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                _active={{ bg: 'rgba(255, 83, 83, 0.2)' }}
                size="sm"
                m="1"
                icon={<Icon as={IoMdClose} color="secondary.700" height="18px" width="18px" />}
                isLoading={
                  cherryPickAction === 'single-decline' &&
                  selectedShifts?.includes(data?.row?.original?.id as string) &&
                  updateShiftStatus === 'loading'
                }
                onClick={() => handleDecline(data?.row?.original?.id)}
              />
            </Tooltip>
          </Flex>
        );
      },
    },
  ];

  useEffect(() => {
    if (searchQuery.get('action') === 'pick') {
      refetchShiftsData();
    }

    if (userInfo?.email !== searchQuery.get('email') && searchQuery.get('email')) {
      toaster.error({
        title: 'Another user is currently logged in.',
        description: `Please login with ${searchQuery.get('email')} to cherry pick shifts`,
        duration: 6000,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refetchShiftsData = () => {
    const postData = { code: searchQuery.get('code') || '', shift: searchQuery.get('shift') || '' };
    mutateCherryPickShifts(postData);
  };

  const handleAccept = (id: string) => {
    setCherryPickAction('single-accept');
    setSelectedShifts([id]);
    mutateUpdateShift({ id: [id], status: true }).then(() => {
      refetchShiftsData();
    });
  };

  const handleDecline = (id: string) => {
    setCherryPickAction('single-decline');
    setSelectedShifts([id]);
    mutateUpdateShift({ id: [id], status: false }).then(() => {
      refetchShiftsData();
    });
  };

  const handleBulkAccept = (selectedFlatRows: any) => {
    setCherryPickAction('bulk-accept');
    const selectedShiftsIds = selectedFlatRows.map((row: any) => row.original?.id);
    setSelectedShifts(selectedShiftsIds);
    mutateUpdateShift({ id: selectedShiftsIds, status: true }).then(() => {
      refetchShiftsData();
    });
  };

  const handleBulkDecline = (selectedFlatRows: any) => {
    const selectedShiftsIds = selectedFlatRows.map((row: any) => row.original?.id);
    setSelectedShifts(selectedShiftsIds);
    setCherryPickAction('bulk-decline');
    mutateUpdateShift({ id: selectedShiftsIds, status: false }).then(() => {
      refetchShiftsData();
    });
  };

  return (
    <Box mx={{ base: 0, sm: 4, md: 6 }} my={{ base: 4, md: 6 }} minHeight="100%" p="4" bg="white" borderRadius="lg">
      <Heading variant="h2" size="md" fontWeight="bold" lineHeight="1.3">
        {t('common:cherryPickShifts')}
      </Heading>
      <Box mt={{ base: '4', xl: '0' }} alignSelf={'flex-end'} justifyContent="flex-end" flexWrap={'wrap'}>
        <Table
          data={data?.payload || []}
          columns={columns}
          isLoading={cherryPickShiftsStatus === 'loading'}
          enableRowSelect
          renderTableFooter={(selectedFlatRows: any, toggleAllRowSelected: any) => {
            return (
              <ShiftTableFooter
                selectedFlatRows={selectedFlatRows}
                toggleAllRowSelected={toggleAllRowSelected}
                handleBulkAccept={handleBulkAccept}
                handleBulkDecline={handleBulkDecline}
                cherryPickAction={cherryPickAction}
                updateShiftStatus={updateShiftStatus}
              />
            );
          }}
        />
      </Box>
    </Box>
  );
};

export default CherryPickShift;
